class Corrector extends CorrectorDelegator {

	///////////////////////////////////////////////////////////////////////////////////////////
	//  CONSTRUCTOR
	///////////////////////////////////////////////////////////////////////////////////////////

	/*
	CONSTRUCTOR: Creates an instance of the Corrector. Requires a DOM Element in which to watch for corrections as well as any options.

	NOTE: If the Corrector is not supported by the current browser it will not perform any setup and simply return a basic object.
	This allows plugins to still be loaded but will not function as expected.
	It is reccomended to call Corrector.supported() before creating the instance or using the Unsupported plugin which will notify users that the Corrector will not work.

	element - A DOM Element in which to correct.
	options - An options Object. NOTE: There are currently no user options.

	Examples:

	corrector = new Corrector(document.body)
	// Example of checking for support.
	if (Corrector.supported()) {
		corrector = new Corrector(document.body)
	}
	else {
		// Fallback for unsupported browsers.
	}

	Returns a new instance of the Corrector.
	*/
	constructor(element, options) {

		// Configuration options
		const defaultOptions = { readOnly: false };
		super(element, $.extend(defaultOptions, options));

		this.plugins = {};
		this.editor = null;
		this.viewer = null;
		this.selectedRanges = null;
		this.mouseIsDown = false;
		this.ignoreMouseUp = false;
		this.viewerHideTimer = false;

		// Return early if the corrector is not supported.
		if (!Corrector.supported()) {
			return this;
		}

		if (!this.options.readOnly) {
			this._setupDocumentEvents();
		}

		this._setupWrapper()._setupViewer()._setupEditor();
		this._setupDynamicStyle();

		// Create adder
		this.adder = $(this.html.adder).appendTo(this.wrapper).hide();

		Corrector._instances.push(this);

	}

	properties() {
		super.properties();

		// Events to be bound on Corrector#element.
		this.events = this.events || {};
		this.events = $.extend(this.events, {
			".corrector-adder button click": "onAdderClick",
			".corrector-adder button mousedown": "onAdderMousedown",
			".corrector-highlight mouseover": "onHighlightMouseover",
			".corrector-highlight mouseout": "startViewerHideTimer"
		});

		this.html = this.html || {};
		this.html = $.extend(this.html, {
			adder: '<div class="corrector-adder"><button type="button">' + _t('Add') + '</button></div>',
			wrapper: '<div class="corrector-wrapper"></div>'
		});
	}

	///////////////////////////////////////////////////////////////////////////////////////////
	//  PRIVATE METHODS
	///////////////////////////////////////////////////////////////////////////////////////////

	/*
	Wraps the children of @element in a @wrapper div.
	NOTE: This method will also remove any script elements inside @element to prevent them re-executing.

	Returns itself to allow chaining.
	*/
	_setupWrapper() {

		this.wrapper = $(this.html.wrapper);

		// We need to remove all scripts within the element before wrapping the contents within a div.
		// Otherwise when scripts are reappended to the DOM they will re-execute.
		// This is an issue for scripts that call document.write() - such as ads - as they will clear the page.
		this.element.find('script').remove();
		this.element.wrapInner(this.wrapper);
		this.wrapper = this.element.find('.corrector-wrapper');

		return this;

	}

	/*
	Creates an instance of CorrectionsViewer and assigns it to the @viewer property, appends it to the @wrapper and sets up event listeners.

	Returns itself to allow chaining.
	*/
	_setupViewer() {
		this.viewer = new CorrectionsViewer({ readOnly: this.options.readOnly });

		this.viewer.hide()
			.on("edit", this.onEditCorrection.bind(this))
			.on("delete", this.onDeleteCorrection.bind(this))
			.addField({
				load: function(field, correction){
					if (correction.text) {
						$(field).html(CorrectorUtil.escape(correction.text));
					}
					else {
						$(field).html("<i>" + _t('No Comment') + "</i>");
					}
					this.publish('correctionViewerTextField', [field, correction]);
				}.bind(this)
			})
			.element.appendTo(this.wrapper).on({
			"mouseover": this.clearViewerHideTimer.bind(this),
			"mouseout": this.startViewerHideTimer.bind(this)
		});

		return this;
	}


	/*
	Creates an instance of the CorrectionsEditor and assigns it to @editor.
	Appends this to the @wrapper and sets up event listeners.

	Returns itself for chaining.
	*/
	_setupEditor() {
		this.editor = new CorrectionsEditor();

		this.editor.hide()
			.on('hide', this.onEditorHide.bind(this))
			.on('save', this.onEditorSubmit.bind(this))
			.addField({
				type: 'textarea',
				label: _t('Comments') + '\u2026',
				load: function(field, correction) {
					$(field).find('textarea').val(correction.text || '');
				},
				submit: function(field, correction) {
					correction.text = $(field).find('textarea').val();
				}
			});
		this.editor.element.appendTo(this.wrapper);

		return this;
	}

	/*
	Sets up the selection event listeners to watch mouse actions on the document.

	Returns itself for chaining.
	*/
	_setupDocumentEvents() {
		$(document).on({
			"mouseup": this.checkForEndSelection.bind(this),
			"mousedown": this.checkForStartSelection.bind(this)
		})
		return this;
	}

	/*
	Sets up any dynamically calculated CSS for the Corrector.

	Returns itself for chaining.
	*/
	_setupDynamicStyle() {
		let style = $('#corrector-dynamic-style');
		if (!style.length) {
			style = $('<style id="corrector-dynamic-style"></style>').appendTo(document.head);
		}

		let sel = '*';
		for (const x of ['adder', 'outer', 'notice', 'filter']) {
			sel += ":not(.corrector-" + x + ")";
		}

		// use the maximum z-index in the page
		let max = CorrectorUtil.maxZIndex( $(document.body).find(sel) );

		// but don't go smaller than 1010, because this isn't bulletproof --
		// dynamic elements in the page (notifications, dialogs, etc.) may well have high z-indices that we can't catch using the above method.
		max = Math.max(max, 1000);

		style.text([
			".corrector-adder, .corrector-outer, .corrector-notice { z-index: #{max + 20}; }",
			".corrector-filter { z-index: #{max + 10}; }"
		].join("\n"));

		return this;
	}

	///////////////////////////////////////////////////////////////////////////////////////////
	//  PUBLIC METHODS
	///////////////////////////////////////////////////////////////////////////////////////////

	/*
	PUBLIC: Destroy the current Corrector instance, unbinding all events and disposing of all relevant elements.

	Returns nothing.
	*/
	destroy() {
		super.destroy();

		$(document).unbind({
			"mouseup":   this.checkForEndSelection,
			"mousedown": this.checkForStartSelection
		});

		$('#corrector-dynamic-style').remove();

		this.adder.remove();
		this.viewer.destroy();
		this.editor.destroy();

		this.wrapper.find('.corrector-highlight').each(function(){
			$(this).contents().insertBefore(this);
			$(this).remove();
		});

		this.wrapper.contents().insertBefore(this.wrapper);
		this.wrapper.remove();
		this.element.data('corrector', null);

		let idx = Corrector._instances.indexOf(this)
		if (idx !== -1) {
			Corrector._instances.splice(idx, 1);
		}

	}

	/*
	PUBLIC: Gets the current selection excluding any nodes that fall outside of the @wrapper. Then returns and Array of CorrectorNormalizedRange instances.

	Examples:

	// A selection inside @wrapper
	correction.getSelectedRanges()
	// Returns [CorrectorNormalizedRange]

	// A selection outside of @wrapper
	correction.getSelectedRanges()
	// Returns []

	Returns Array of CorrectorNormalizedRange instances.
	*/
	getSelectedRanges() {

		let selection = CorrectorUtil.getSelection();
		let ranges = [];
		let rangesToIgnore = [];

		let selectionIsCollapsed = (typeof selection.collapsed !== 'undefined') ? selection.collapsed : selection.isCollapsed;

		if (!selectionIsCollapsed) {

			for (let i = 0; i < selection.rangeCount; i++) {
				let r = selection.getRangeAt(i)
				let browserRange = new CorrectorBrowserRange(r);
				let normedRange = browserRange.normalize().limit(this.wrapper[0]);

				/*
				If the new range falls fully outside the wrapper, we should add it back to the document but not return it from this method
				*/
				if (normedRange === null) {
					rangesToIgnore.push(r);
				}

				ranges.push(normedRange);

			}

			selection.removeAllRanges();

			for(const r of rangesToIgnore) {
				selection.addRange(r);
			}

			// Remove any ranges that fell outside of _self.wrapper.
			return $.grep(ranges, function(range){
				// Add the normed range back to the selection if it exists.
				if (range) {
					selection.addRange( range.toRange() );
				}
				return range;
			});

		}
		else {
			return [];
		}

	}

	/*
	Public: Creates and returns a new correction object.
	Publishes the 'beforeCorrectionCreated' event to allow the new correction to be modified.

	Examples:

	corrector.createCorrection()// Returns {}
	corrector.on 'beforeCorrectionCreated', function(correction){
		correction.myProperty = 'This is a custom property'
	}
	corrector.createCorrection()// Returns {myProperty: "This is a…"}

	Returns a newly created correction Object.
	*/
	createCorrection() {
		let correction = {};
		this.publish('beforeCorrectionCreated', [correction]);
		return correction;
	}

	/*
	PUBLIC: Initialises an correction either from an object representation or an correction created with Corrector#createCorrection().
	It finds the selected range and higlights the selection in the DOM.

	correction - An correction Object to initialise.

	Examples:

	// Create a brand new correction from the currently selected text.
	correction = corrector.createCorrection()
	correction = corrector.setupCorrection(correction)
	// correction has now been assigned the currently selected range and a highlight appended to the DOM.

	// Add an existing correction that has been stored elsewere to the DOM.
	correction = getStoredCorrectionWithSerializedRanges()
	correction = corrector.setupCorrection(correction)

	Returns the initialised correction.
	*/
	setupCorrection(correction) {

		let root = this.wrapper[0];
		correction.ranges = correction.ranges || this.selectedRanges;

		let normedRanges = [];
		for (const r of correction.ranges) {
			try {
				normedRanges.push(CorrectorRange.sniff(r).normalize(root));
			}
			catch(e) {
				if (e instanceof CorrectorRangeError) {
					this.publish('rangeNormalizeFail', [correction, r, e]);
				}
				else {
					// Oh Javascript, why you so crap? This will lose the traceback.
					throw e;
				}
			}
		}

		correction.quote = [];
		correction.ranges = [];
		correction.highlights = [];

		for (const normed of normedRanges) {
			correction.quote.push( $.trim(normed.text()) );
			correction.ranges.push( normed.serialize(this.wrapper[0], '.corrector-highlight') );
			$.merge( correction.highlights, this.highlightRange(normed) );
		}

		// Join all the quotes into one string.
		correction.quote = correction.quote.join(' / ');

		// Save the correction data on each highlighter element.
		$(correction.highlights).data('corrector', correction);
		$(correction.highlights).attr('data-corrector-id', correction.id);

		return correction;

	}

	/*
	PUBLIC: Publishes the 'beforeCorrectionUpdated' and 'correctionUpdated' events.
	Listeners wishing to modify an updated correction should subscribe to 'beforeCorrectionUpdated' while listeners storing corrections should subscribe to 'correctionUpdated'.

	correction - An correction Object to update.

	Examples:

	correction = {tags: 'apples oranges pears'}
	corrector.on 'beforeCorrectionUpdated', function(correction){
		// validate or modify a property.
		correction.tags = correction.tags.split(' ')
	}
	corrector.updateCorrection(correction)
	// Returns ["apples", "oranges", "pears"]

	Returns correction Object.
	*/
	updateCorrection(correction) {
		this.publish('beforeCorrectionUpdated', [correction]);
		$(correction.highlights).attr('data-corrector-id', correction.id);
		this.publish('correctionUpdated', [correction]);
		return correction;
	}

	/*
	PUBLIC: Deletes the correction by removing the highlight from the DOM.
	Publishes the 'correctionDeleted' event on completion.

	correction - An correction Object to delete.

	Returns deleted correction.
	*/
	deleteCorrection(correction) {
		if (correction.highlights !== null) {
			for (const h of correction.highlights) {
				if (h.parentNode === null) {
					return true;
				}
				let child = h.childNodes[0];
				$(h).replaceWith(h.childNodes);
			}
		}
		this.publish('correctionDeleted', [correction]);
		return correction;
	}

	/*
	PUBLIC: Loads an Array of corrections into the @element.
	Breaks the task into chunks of 10 corrections.

	corrections - An Array of correction Objects.

	Examples:

	loadCorrectionsFromStore function(corrections){
		corrector.loadCorrections(corrections)
	}

	Returns itself for chaining.
	*/
	loadCorrections(corrections) {
		if (corrections == null) {
			corrections = [];
		}

		let loader = annList => {
			if (annList == null) {
				annList = [];
			}
			const now = annList.splice(0,10);

			for (let n of now) {
				this.setupCorrection(n);
			}

			// If there are more to do, do them after a 10ms break (for browser
			// responsiveness).
			if (annList.length > 0) {
				return setTimeout((() => loader(annList)), 10);
			}
			else {
				return this.publish('correctionsLoaded', [clone]);
			}
		};

		let clone = corrections.slice();
		loader(corrections);

		return this;
	}


	/*
	PUBLIC: Calls the Store#dumpCorrections() method.

	Returns dumped corrections Array or false if Store is not loaded.
	*/
	dumpCorrections() {
		if (this.plugins['Store']) {
			return this.plugins['Store'].dumpCorrections();
		}
		else {
			console.warn( _t("Can't dump corrections without Store plugin.") );
			return false;
		}
	}

	/*
	PUBLIC: Wraps the DOM Nodes within the provided range with a highlight element of the specified class and returns the highlight Elements.

	normedRange - A CorrectorNormalizedRange to be highlighted.
	cssClass - A CSS class to use for the highlight (default: 'corrector-highlight')

	Returns an array of highlight Elements.
	*/
	highlightRange(normedRange, cssClass='corrector-highlight') {
		const white = /^\s*$/;
		let hl = $(`<span class="${cssClass}"></span>`);

		/*
		Ignore text nodes that contain only whitespace characters. This prevents spans being injected between elements that can only contain a restricted subset of nodes such as table rows and lists.
		This does mean that there may be the odd abandoned whitespace node in a paragraph that is skipped but better than breaking table layouts.
		*/

		let highlights = [];

		for (const node of normedRange.textNodes()) {
			if (!white.test(node.nodeValue)) {
				highlights.push( $(node).wrapAll(hl).parent().show()[0] );
			}
		}

		return highlights;
	}

	/*
	PUBLIC: highlight a list of ranges

	normedRanges - An array of CorrectorNormalizedRanges to be highlighted.
	cssClass - A CSS class to use for the highlight (default: 'corrector-highlight')

	Returns an array of highlight Elements.
	*/
	highlightRanges(normedRanges, cssClass='corrector-highlight') {
		let highlights = [];

		for (const r of normedRanges) {
			$.merge( highlights, this.highlightRange(r, cssClass) );
		}

		return highlights;
	}


	/*
	PUBLIC: Registers a plugin with the Corrector.
	A plugin can only be registered once. The plugin will be instantiated in the following order.

	1. A new instance of the plugin will be created (providing the @element and options as params) then assigned to the @plugins registry.
	2. The current Corrector instance will be attached to the plugin.
	3. The Plugin#pluginInit() method will be called if it exists.

	name    - Plugin to instantiate. Must be in the CorrectorPlugins namespace.
	options - Any options to be provided to the plugin constructor.

	Examples:

	corrector
		.addPlugin('Tags')
		.addPlugin('Store', {
			prefix: '/store'
		})
		.addPlugin('Permissions', {
			user: 'Bill'
		})

	Returns itself to allow chaining.
	*/
	addPlugin(name, options) {
		if (this.plugins[name]) {
			console.error( _t("You cannot have more than one instance of any plugin.") );
		}
		else {
			const klass = CorrectorPlugin[name];
			if (typeof klass === 'function') {
				this.plugins[name] = new klass(this.element[0], options);
				this.plugins[name].corrector = this;
				if (typeof this.plugins[name].pluginInit === 'function') {
					this.plugins[name].pluginInit();
				}
			}
			else {
				console.error( _t("Could not load ") + name + _t(" plugin. Have you included the appropriate <script> tag?") );
			}
		}
		return this;
	}


	/*
	PUBLIC: Loads the @editor with the provided correction and updates its position in the window.

	correction - An correction to load into the editor.
	location   - Position to set the Editor in the form {top: y, left: x}

	Examples:

	corrector.showEditor({text: "my comment"}, {top: 34, left: 234})

	Returns itself to allow chaining.
	*/
	showEditor(correction, location) {
		this.editor.element.css(location);
		this.editor.load(correction);
		this.publish('correctionEditorShown', [this.editor, correction]);
		return this;
	}

	/*
	Public: Loads the @viewer with an Array of corrections and positions it at the location provided.
	Calls the 'correctionViewerShown' event.

	correction - An Array of corrections to load into the viewer.
	location   - Position to set the Viewer in the form {top: y, left: x}

	Examples:

	corrector.showViewer(
		[{text: "my comment"}, {text: "my other comment"}],
		{top: 34, left: 234})
	)

	Returns itself to allow chaining.
	*/
	showViewer(corrections, location) {
		this.viewer.element.css(location);
		this.viewer.load(corrections);
		return this.publish('correctionsViewerShown', [this.viewer, corrections]);
	}


	/*
	PUBLIC: Determines if the provided element is part of the corrector plugin.
	Useful for ignoring mouse actions on the corrector elements.
	NOTE: The @wrapper is not included in this check.

	element - An Element or TextNode to check.

	Examples:

	span = document.createElement('span')
	corrector.isCorrector(span)// Returns false

	corrector.isCorrector(corrector.viewer.element) // Returns true

	Returns true if the element is a child of a corrector element.
	*/
	isCorrector(element) {
		return !!$(element).parents().addBack().filter('[class^=corrector-]').not('[class^=corrector-highlight]').not(this.wrapper).length;
	}


	///////////////////////////////////////////////////////////////////////////////////////////
	//  CALLBACKS
	///////////////////////////////////////////////////////////////////////////////////////////

	/*
	Callback method called when the @editor fires the "hide" event.
	Itself publishes the 'correctionEditorHidden' event and resets the @ignoreMouseup property to allow listening to mouse events.

	Returns nothing.
	*/
	onEditorHide() {
		this.publish('correctionEditorHidden', [this.editor]);
		this.ignoreMouseup = false;
	}

	/*
	Callback method called when the @editor fires the "save" event.
	Itself publishes the 'correctionEditorSubmit' event and creates/updates the edited correction.

	Returns nothing.
	*/
	onEditorSubmit(correction) {
		this.publish('correctionEditorSubmit', [this.editor, correction]);
	}

	/*
	Corrector#element event callback. Allows 250ms for mouse pointer to get from correction highlight to @viewer to manipulate corrections.
	If timer expires the @viewer is hidden.

	Returns nothing.
	*/
	startViewerHideTimer() {
		// Don't do this if timer has already been set by another correction
		if (!this.viewerHideTimer && this.viewer) {
			this.viewerHideTimer = setTimeout(() => this.viewer.hide(), 250);
		}
	}

	/*
	Viewer#element event callback.
	Clears the timer set by Corrector#startViewerHideTimer() when the @viewer is moused over.

	Returns nothing.
	*/
	clearViewerHideTimer() {
		clearTimeout(this.viewerHideTimer);
		this.viewerHideTimer = false;
	}

	/*
	Corrector#element callback.
	Sets the @mouseIsDown property used to determine if a selection may have started to true.
	Also calls Corrector#startViewerHideTimer() to hide the Corrector#viewer.

	event - A mousedown Event object.

	Returns nothing.
	*/
	checkForStartSelection(event) {
		if (!event || !this.isCorrector(event.target)) {
			this.startViewerHideTimer();
		}
		this.mouseIsDown = true;
	}

	/*
	Corrector#element callback.
	Checks to see if a selection has been made on mouseup and if so displays the Corrector#adder.
	If @ignoreMouseup is set will do nothing. Also resets the @mouseIsDown property.
	
	event - A mouseup Event object.
	
	Returns nothing.
	*/
	checkForEndSelection(event) {
		this.mouseIsDown = false;

		// This prevents the note image from jumping away on the mouseup of a click on icon.
		if (this.ignoreMouseUp) {
			return false;
		}

		// Get the currently selected ranges.
		this.selectedRanges = this.getSelectedRanges();

		for (const range of this.selectedRanges) {
			const container = range.commonAncestor;
			if (this.isCorrector(container)) {
				return false;
			}
		}

		if (event && this.selectedRanges.length) {
			this.adder.css(CorrectorUtil.mousePosition(event, this.wrapper[0])).show();
		}
		else {
			this.adder.hide();
		}
	}

	/*
	Corrector#element callback. Displays viewer with all corrections associated with highlight Elements under the cursor.

	event - A mouseover Event object.

	Returns nothing.
	*/
	onHighlightMouseover(event) {
		// Cancel any pending hiding of the viewer.
		this.clearViewerHideTimer();

		// Don't do anything if we're making a selection
		if (this.mouseIsDown) {
			return false;
		}

		// If the viewer is already shown, hide it first
		if (this.viewer.isShown()) {
			this.viewer.hide();
		}

		let corrections = $(event.target)
			.parents('.corrector-highlight')
			.addBack()
			.map(function(){
				return $(this).data("corrector");
			})
			.toArray();

		// Now show the viewer with the wanted corrections
		this.showViewer(corrections, CorrectorUtil.mousePosition(event, this.wrapper[0]));
	}

	/*
	Corrector#element callback. Sets @ignoreMouseup to true to prevent the correction selection events firing when the adder is clicked.

	event - A mousedown Event object

	Returns nothing.
	*/
	onAdderMousedown(event) {
		CorrectorUtil.preventEventDefault(event);
		this.ignoreMouseup = true;
	}


	/*
	Corrector#element callback. Displays the @editor in place of the @adder and loads in a newly created correction Object.
	The click event is used as well as the mousedown so that we get the :active state on the @adder when clicked

	event - A mousedown Event object

	Returns nothing.
	*/
	onAdderClick(event) {
		CorrectorUtil.preventEventDefault(event);

		// Hide the adder
		const position = this.adder.position();
		this.adder.hide();

		// Show a temporary highlight so the user can see what they selected.
		// Also extract the quotation and serialize the ranges

		const correction = this.setupCorrection(this.createCorrection());
		$(correction.highlights).addClass('corrector-highlight-temporary');

		// Subscribe to the editor events

		const _this = this;
		// Make the highlights permanent if the correction is saved
		const save = () => {
			cleanup();
			$(correction.highlights).removeClass('corrector-highlight-temporary');
			_this.publish('correctionCreated', [correction]);
		};

		// Remove the highlights if the edit is cancelled
		const cancel = () => {
			cleanup();
			_this.deleteCorrection(correction);
		};

		// Don't leak handlers at the end
		const cleanup = () => {
			_this.unsubscribe('correctionEditorHidden', cancel);
			_this.unsubscribe('correctionEditorSubmit', save);
		};

		this.subscribe('correctionEditorHidden', cancel);
		this.subscribe('correctionEditorSubmit', save);

		// Display the editor.
		this.showEditor(correction, position);
	}


	/*
	Corrector#viewer callback function.
	Displays the Corrector#editor in the positions of the Corrector#viewer and loads the passed correction for editing.

	correction - An correction Object for editing.

	Returns nothing.
	*/
	onEditCorrection(correction) {
		const offset = this.viewer.element.position();

		// Subscribe once to editor events

		// Update the correction when the editor is saved
		const _this = this;

		const update = function(){
			cleanup();
			_this.updateCorrection(correction);
		};

		// Remove handlers when the editor is hidden
		const cleanup = function(){
			_this.unsubscribe('correctionEditorHidden', cleanup);
			_this.unsubscribe('correctionEditorSubmit', update);
		};

		this.subscribe('correctionEditorHidden', cleanup);
		this.subscribe('correctionEditorSubmit', update);

		// Replace the viewer with the editor
		this.viewer.hide();
		this.showEditor(correction, offset);
	}

	/*
	Corrector#viewer callback function.
	Deletes the correction provided to the callback.

	correction - An correction Object for deletion.

	Returns nothing.
	*/
	onDeleteCorrection(correction) {
		this.viewer.hide();

		// Delete highlight elements.
		this.deleteCorrection(correction);
	}

	///////////////////////////////////////////////////////////////////////////////////////////
	//  STATIC METHODS
	///////////////////////////////////////////////////////////////////////////////////////////

}

// Create namespace for Corrector plugins
class CorrectorPlugin extends CorrectorDelegator {
	constructor(element, options) {
		super(element, options);
	}
	properties() {
		super.properties();
	}
	pluginInit(){}
}

// Sniff the browser environment and attempt to add missing functionality.
const g = CorrectorUtil.getGlobal()

if (((g.document != null ? g.document.evaluate : undefined) == null)) {
	$.getScript('/wp-content/themes/ielts-writing-assist/js/vendor/xpath.js');
}

if ((g.JSON == null)) {
	$.getScript('/wp-content/themes/ielts-writing-assist/js/vendor/json2.js');
}

// Ensure the Node constants are defined
if ((g.Node == null)) {
	g.Node = {
		ELEMENT_NODE                :  1,
		ATTRIBUTE_NODE              :  2,
		TEXT_NODE                   :  3,
		CDATA_SECTION_NODE          :  4,
		ENTITY_REFERENCE_NODE       :  5,
		ENTITY_NODE                 :  6,
		PROCESSING_INSTRUCTION_NODE :  7,
		COMMENT_NODE                :  8,
		DOCUMENT_NODE               :  9,
		DOCUMENT_TYPE_NODE          : 10,
		DOCUMENT_FRAGMENT_NODE      : 11,
		NOTATION_NODE               : 12
	};
}

// Store a reference to the current Corrector object.
_Corrector = Corrector;

// Bind our local copy of jQuery so plugins can use the extensions.
Corrector.$ = $

// Export other modules for use in plugins.
Corrector.Delegator = CorrectorDelegator;
Corrector.Range = CorrectorRange;
Corrector.Util = CorrectorUtil;

// Expose a global instance registry
Corrector._instances = [];

// Bind gettext helper so plugins can use localisation.
Corrector._t = _t;

// Returns true if the Corrector can be used in the current browser.
Corrector.supported = () => (function() { return !!this.getSelection; })();

// Restores the Corrector property on the global object to it's previous value and returns the Corrector.
Corrector.noConflict = function() {
	CorrectorUtil.getGlobal().Corrector = _Corrector;
	return this;
};
